// src/pages/auth/AccountDeactivated.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, MessageSquare, User, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { messagesApi } from '../../api/messages-api';
import { authApi } from '../../api/auth-api';
import { toast } from 'react-toastify';

const AccountDeactivated = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    subject: 'Réactivation de compte désactivé',
    category: 'ACCOUNT',
    priority: 'HIGH',
    content: ''
  });

  // Get user data if available (deactivatedUserData has the freshest info including blockReason)
  const deactivatedData = authApi.getDeactivatedUserData();
  const userData = authApi.getUserData() || deactivatedData;
  const blockReason = deactivatedData?.blockReason || null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    
    if (!formData.content.trim()) {
      toast.error('Veuillez décrire votre problème');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create support ticket
      const ticketData = {
        subject: formData.subject,
        category: formData.category,
        priority: formData.priority,
        status: 'OPEN',
        // If user data is available, include it, otherwise it will be anonymous
        userId: userData?.id || null,
        firstMessage: {
          content: formData.content,
          adminReply: false
        }
      };

      await messagesApi.createTicket(ticketData);
      
      setTicketSubmitted(true);
      toast.success('Votre demande a été soumise avec succès');
    } catch (error) {
      console.error('Error creating support ticket:', error);
      toast.error('Erreur lors de l\'envoi de votre demande. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    authApi.logout();
    window.location.href = '/admin/login';
  };

  if (ticketSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md text-center">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>
          
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            Demande envoyée
          </h2>
          
          <p className="text-gray-600 mb-6">
            Votre demande de réactivation a été envoyée à notre équipe de support. 
            Nous vous contacterons dans les plus brefs délais.
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-800 mb-2">Prochaines étapes :</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Notre équipe examinera votre demande</li>
              <li>• Vous recevrez une réponse par email</li>
              <li>• Le processus prend généralement 24-48h</li>
            </ul>
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full p-8 bg-white rounded-lg shadow-md">
        
          <>
            {/* Main deactivated account message */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>
              
              <h2 className="text-2xl font-semibold text-gray-800 mb-2">
                Compte désactivé
              </h2>
              
              <p className="text-gray-600 mb-4">
                Votre compte a été temporairement désactivé. Vous ne pouvez pas accéder à la plateforme pour le moment.
              </p>
            </div>

            {/* User info if available */}
            {userData && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {userData.firstName && userData.lastName 
                        ? `${userData.firstName} ${userData.lastName}`
                        : 'Utilisateur'
                      }
                    </p>
                    <p className="text-sm text-gray-600">{userData.email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Reason for deactivation */}
            {blockReason ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-red-800 mb-1">Raison :</h3>
                <p className="text-sm text-red-700">{blockReason}</p>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-yellow-800 mb-2">Causes possibles :</h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Violation des conditions d'utilisation</li>
                  <li>• Activité suspecte détectée</li>
                  <li>• Demande de sécurité</li>
                  <li>• Maintenance du compte</li>
                </ul>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-3">              
              <button
                onClick={handleLogout}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Retour à la connexion
              </button>
            </div>
          </>
      </div>
    </div>
  );
};

export default AccountDeactivated;